"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Stethoscope, HeartHandshake, ArrowUpRight, Mail, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const editorialHubs = [{
  title: "Species & Care",
  links: [{
    label: "Dog Health & Care",
    to: "/DogsPage"
  }, {
    label: "Cat Health & Care",
    to: "/CatsPage"
  }, {
    label: "Breed Profiles",
    to: "/BreedsPage"
  }, {
    label: "Behavior & Training",
    to: "/BehaviorTrainingPage"
  }]
}, {
  title: "Clinical Topics",
  links: [{
    label: "Pet Health Guidance",
    to: "/PetHealthPage"
  }, {
    label: "Nutrition & Diets",
    to: "/NutritionPage"
  }, {
    label: "Gear & Product Reviews",
    to: "/ProductsReviewsPage"
  }, {
    label: "Search Publication",
    to: "/SearchResultsPage"
  }]
}, {
  title: "Publication",
  links: [{
    label: "About PetsWhirl",
    to: "/AboutPetsWhirlPage"
  }, {
    label: "Editorial Standards",
    to: "/AboutPetsWhirlPage"
  }, {
    label: "Veterinary Board",
    to: "/AboutPetsWhirlPage"
  }, {
    label: "Search Index",
    to: "/SearchResultsPage"
  }]
}];
function RecursiveMenuItems({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-border/40"}>
      {items.map((item, i) => <li key={item.id} data-index={i} className="text-sm">
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-foreground transition-colors duration-150 inline-flex items-center gap-1 group">
            <span>{item.label}</span>
            {item.children && item.children.length > 0 && <ChevronRight className="size-3 text-muted-foreground/60 group-hover:translate-x-0.5 transition-transform" />}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMenuItems items={item.children} level={level + 1} />}
        </li>)}
    </ul>;
}
function DynamicFooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2 py-1">
        <div className="h-4 w-24 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-4 w-32 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-4 w-28 bg-muted/20 animate-pulse rounded-sm" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  return <RecursiveMenuItems items={menuItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="footer" data-nav="dark" className="dark bg-background text-foreground border-t border-border/40 relative overflow-hidden">
      <footer className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        {/* Clinical Governance Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="mb-14 rounded-xl border border-border bg-card/40 p-6 md:p-8 backdrop-blur-xs">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-accent/10 border border-accent/20 p-2.5 shrink-0 text-accent">
                <Stethoscope className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-base font-semibold text-foreground tracking-tight">
                  Veterinary-Reviewed
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Every health topic and nutritional guide is audited against current clinical standards.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-accent/10 border border-accent/20 p-2.5 shrink-0 text-accent">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-base font-semibold text-foreground tracking-tight">
                  Evidence-Informed
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Grounded in peer-reviewed veterinary literature, clinical trials, and verified animal science.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-accent/10 border border-accent/20 p-2.5 shrink-0 text-accent">
                <HeartHandshake className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-base font-semibold text-foreground tracking-tight">
                  Independent Insights
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Unbiased product reviews and nutritional comparisons without commercial sponsor influence.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Primary Footer Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pb-14 border-b border-border/40">
          {/* Brand & Mission Statement Column */}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center">
              <Link to="/" className="inline-block">
                <WvcLogo className="h-8 w-auto text-foreground" />
              </Link>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              An authoritative pet wellness publication providing calm, evidence-informed guidance on dog and cat health, nutrition, behavior, and care.
            </p>

            <div className="pt-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">
                Editorial Inquiries
              </div>
              <a href="mailto:editorial@petswhirl.com" className="inline-flex items-center gap-2 text-sm text-foreground hover:text-accent transition-colors">
                <Mail className="size-4 text-muted-foreground" />
                <span>editorial@petswhirl.com</span>
              </a>
            </div>
          </div>

          {/* Desktop Navigation Links Columns */}
          <div className="hidden md:grid md:col-span-8 grid-cols-3 gap-8">
            {editorialHubs.map((hub, hubIndex) => <div key={hub.title} data-index={hubIndex} className="space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground/90 font-default">
                  {hub.title}
                </h4>
                <ul className="space-y-2.5">
                  {hub.links.map((link, linkIndex) => <li key={link.label} data-index={linkIndex}>
                      <Link to={link.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 inline-flex items-center gap-1 group">
                        <span>{link.label}</span>
                        <ArrowUpRight className="size-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 group-hover:text-foreground transition-all duration-150" />
                      </Link>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Mobile Accordion Navigation */}
          <div className="block md:hidden lg:col-span-8">
            <Accordion type="single" collapsible className="w-full divide-y divide-border/40">
              {editorialHubs.map((hub, hubIndex) => <AccordionItem key={hub.title} value={`hub-${hubIndex}`} data-index={hubIndex} className="border-border/40">
                  <AccordionTrigger className="text-sm font-semibold text-foreground py-3.5 hover:no-underline">
                    {hub.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2.5 pt-1 pb-2">
                      {hub.links.map((link, linkIndex) => <li key={link.label} data-index={linkIndex}>
                          <Link to={link.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors block py-0.5">
                            {link.label}
                          </Link>
                        </li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>)}

              <AccordionItem value="hub-navigation" data-index={99} className="border-border/40">
                <AccordionTrigger className="text-sm font-semibold text-foreground py-3.5 hover:no-underline">
                  Quick Navigation
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-1 pb-2">
                    <MenuProvider menu_id="17">
                      <DynamicFooterMenu />
                    </MenuProvider>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </motion.div>

        {/* Clinical Disclaimer Box */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="mt-8 pt-2">
          <div className="rounded-lg border-l-2 border-accent bg-muted/20 p-4 text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-semibold mr-1">Veterinary Medical Disclaimer:</strong>
            The educational content on PetsWhirl is intended solely for general informational purposes and does not substitute for personalized veterinary diagnosis, clinical treatment, or hands-on medical advice. If your dog or cat exhibits acute symptoms, poisoning signs, or severe behavioral shifts, consult a licensed veterinary clinician immediately.
          </div>
        </motion.div>

        {/* Bottom Bar: Copyright & Legal */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="mt-10 pt-6 border-t border-border/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            {"©"} {new Date().getFullYear()} PetsWhirl. All rights reserved.
          </p>

          <div className="flex flex-wrap items-center gap-5">
            <Link to="/AboutPetsWhirlPage" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <span className="text-border/60">{"•"}</span>
            <Link to="/AboutPetsWhirlPage" className="hover:text-foreground transition-colors">
              Terms of Use
            </Link>
            <span className="text-border/60">{"•"}</span>
            <Link to="/AboutPetsWhirlPage" className="hover:text-foreground transition-colors">
              Editorial Governance
            </Link>
            <span className="text-border/60">{"•"}</span>
            <Button variant="ghost" size="sm" asChild className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-foreground hover:bg-transparent">
              <Link to="/SearchResultsPage">Search Topics</Link>
            </Button>
          </div>
        </motion.div>
      </footer>
    </section>;
}